import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type LoginAsInstanceType } from '@/api/organization';
import { Button } from '@/components/buttons';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { Panel } from '@/components/panels';
import { Text } from '@/components/typography';
import { MonthDateYearFormat } from '@/constants/formats';
import { formatDate } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  data?: LoginAsInstanceType[];
  totalResults?: number;
  isLoading?: boolean;
  error: Error | null;
  onLoginAs: (instance: LoginAsInstanceType) => void;
};

const LoginAsTable = ({ data, totalResults, isLoading, error, onLoginAs }: Props) => {
  const columns = useMemo<InstantSearchDataTableColumnDef<LoginAsInstanceType>[]>(
    () => [
      {
        header: t`Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: true,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => row.original.name,
      },
      {
        header: t`Domain`,
        id: 'domain',
        minSize: 140,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.domain,
      },
      {
        header: t`Instance Id`,
        id: 'id',
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.id,
      },
      {
        header: t`Visits L30d`,
        id: 'visitsL30d',
        enableSorting: false,
        size: 170,
        cell: ({ row }) => (
          <Text variant="body2">
            {row.original.visitsL30d == null ? (
              <Trans>Unavailable</Trans>
            ) : (
              numberFormat(row.original.visitsL30d)
            )}
          </Text>
        ),
      },
      {
        header: t`Created On`,
        id: 'createdAt',
        accessorKey: 'createdAt',
        meta: { align: 'end' },
        enableSorting: true,
        size: 140,
        cell: ({ row }) => formatDate(row.original.createdAt, MonthDateYearFormat),
      },
      {
        header: t`Actions`,
        id: 'actions',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => (
          <Button onClick={() => onLoginAs(row.original)}>
            <Trans>Login As</Trans>
          </Button>
        ),
      },
    ],
    [onLoginAs],
  );

  return (
    <Panel
      title={t`Customer Instances`}
      subtitle={t`Visits L30d: processed visits for the last 30 UTC calendar days, including today. Recent visits may not appear yet.`}
      isFullHeight
    >
      <InstantSearchDataTable<LoginAsInstanceType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        emptyMessage={<Trans>No customer instances found.</Trans>}
        error={error}
        getRowId={(record) => record.id}
        initialColumnPinning={{
          start: ['name'],
          end: ['actions'],
        }}
      />
    </Panel>
  );
};

export default LoginAsTable;
